import React from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Users, CalendarDays, Building2, User as UserIcon, ChevronLeft, Download, RefreshCw } from "lucide-react";
import { DateRangeTool } from "../components/DateRange";
import { Button } from "../components/primitives";
import { exportWorkSummary } from "./workSummaryNew/exportPdf";
import { MOCK_DAY, MOCK_DAY_DATA, MOCK_MEMBERS, MOCK_BRIEF } from "./workSummaryNew/mockDay";
// 2026-08-30: department scope — the same page one level up.
import { DepartmentStory } from "./workSummaryNew/department/DepartmentStory";
import { ToolIcon } from "../components/classic/panels";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { PageShell } from "../components/PageShell";
import { Card } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { SERVER_URL } from "../../../utils/serverConfig";
import { useSurface, canvasStyle, ambientStyle } from "./growthSurface";
import { CATS, useCatColors, fmtDur, fmtClock, fmtClockTz, hourLabel, setWorkSummaryClockOffset } from "./workSummaryNew/bits";
import { AppDetail } from "./workSummaryNew/AppDetail";
import { StoryStyles, storyVars, SectionFrame, stripSet } from "./workSummaryNew/story/storyBits";
import { StoryHero } from "./workSummaryNew/story/StoryHero";
import { StoryPulse } from "./workSummaryNew/story/StoryPulse";
// StorySpine ("How the day ran") removed from the page on request — the
// import stays one uncomment away: ./workSummaryNew/story/StorySpine
import { StoryRail, useDayBrief, KIND_META } from "./workSummaryNew/story/StoryRail";
import { StoryChapters, ThreadDeepRead, CAT_ICON } from "./workSummaryNew/story/StoryChapters";
import { StoryFlow } from "./workSummaryNew/story/StoryAccounting";
import { StoryDrawer, DrawerSec, DrawerSpine } from "./workSummaryNew/story/StoryDrawer";
import { TakeawayDeepRead } from "./workSummaryNew/story/TakeawayDeepRead";
import {
  WhatWentInto, threadVerdict,
  WhatWentIntoSk,
} from "./workSummaryNew/story/DayShape";
import { DayJuice, DayJuiceSk } from "./workSummaryNew/story/DayJuice";
import { AiAdoption, aiAdoptionMeta } from "./workSummaryNew/story/AiAdoption";
import { HourGrid, HourLegend } from "./workSummaryNew/story/HourGrid";
import { HourDeepRead, hourDrawerProps } from "./workSummaryNew/story/HourDeepRead";
import { useWorkSummaryReport } from "./workSummaryNew/reportConfig";
import { useOrgProfile } from "../hooks/useOrgProfile";
import { DayOverview } from "./workSummaryNew/DayOverview";

/* Work Summary (new) — the "Day Story" presentation.
   ─────────────────────────────────────────────────────────────────────────
   FULLY ADDITIVE at the API level: the page still draws from the same five
   endpoints (/day for everything on load; /day-brief, /thread-detail,
   /takeaway-detail and /app-detail on demand). Only the presentation moved:

     ⓪ The headline  — who, which day, what kind of day (hero + score ring
                        + 14-day scrubber)
     ① The pulse     — four measures with their own micro-charts
     ② The spine     — activity curve over session blocks, with moments
     ③ The brief     — AI takeaways as a rail; hover lights the spine
     ④ The chapters  — every thread as a card; open one for the deep read
     ⑤ The accounting— category flow + ranked applications + focus lab

   Everything deep opens in ONE right-hand drawer, so reading depth is a
   gesture (click) rather than a page-length problem. */

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function dateToRange(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  const start = new Date(y, m - 1, d);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  const today = fmtDate(new Date());
  const yest = new Date(); yest.setDate(yest.getDate() - 1);
  const key = iso === today ? "today" : iso === fmtDate(yest) ? "yesterday" : "custom";
  return { start, end, key };
}

export default function WorkSummaryNewV2() {
  const t = useDashTheme();
  const C = useCatColors();
  const sf = useSurface();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isMock = params.get("mock") === "1";
  const setMock = (on) => {
    const next = new URLSearchParams(params);
    if (on) next.set("mock", "1"); else next.delete("mock");
    setParams(next, { replace: true });
  };

  const [members, setMembers] = React.useState([]);
  const [loadingMembers, setLoadingMembers] = React.useState(true);
  const [member, setMember] = React.useState(() => params.get("userId") || params.get("member") || null);
  const [date, setDate] = React.useState(() => {
    const q = params.get("date");
    return /^\d{4}-\d{2}-\d{2}$/.test(q || "") ? q : fmtDate(new Date());
  });
  // Internal knob, not a user control: the page always reads both sources
  // merged (app samples win per-minute, agent fills the gaps).
  const source = "both";
  // Which telling of the day is on screen: the story (insights), or the hour grid.
  const orgProfile = useOrgProfile();
  const overviewOn = orgProfile?.workOverviewEnabled === true;
  const [tab, setTab] = React.useState(() => {
    const raw = String(params.get("tab") || "").toLowerCase();
    if (raw === "hourly") return "hourly";
    if (raw === "overview") return "overview";
    return "story";
  });
  /* 2026-08-30: scope. "person" is the page as it was; "department" renders
     DepartmentStory in its place. Opening a person from a department hands
     off to the person scope with the member pre-selected and remembers where
     it came from, so the reader can step back up. */
  const [scope, setScope] = React.useState("person");
  /* DEPT_SCOPE_ORGS (2026-08-30, user): the Department scope is live for ONE
     org for now — MRF (Musaib). Everyone else sees the page exactly as before
     (no switch). Add org ids here to roll it out; the backend allowlist in
     routes/workSummaryNewDept.js must match. */
  const DEPT_SCOPE_ORGS = ["6a158e92ecba51bec473b1c8"];
  const rawOrg = user?.organizationId;
  const orgIdStr = String((rawOrg && typeof rawOrg === "object") ? (rawOrg._id || rawOrg.id || "") : (rawOrg || ""));
  const deptScopeEnabled = DEPT_SCOPE_ORGS.includes(orgIdStr);
  React.useEffect(() => { if (!deptScopeEnabled && scope === "department") setScope("person"); }, [deptScopeEnabled, scope]);
  const [backTo, setBackTo] = React.useState(null); // department name
  const openPersonFromDept = React.useCallback(({ id, department }) => {
    setBackTo(department || null);
    setMember(String(id));
    setScope("person");
    setTab("story");
  }, []);

  const [data, setData] = React.useState(null);
  const [overviewHours, setOverviewHours] = React.useState(null);
  // Org offset for this date. A leftover `UTC` / offset 0 (default, or a
  // stale cache after the org moved to IST) must not pin The day at 5 AM.
  // Fall back to the browser zone until the payload carries a real zone.
  const tzLabel = String(data?.tz?.label || "");
  const apiOff = data?.tz?.offsetMin;
  const tzOffsetMin = (tzLabel && tzLabel !== "UTC" && tzLabel !== "GMT" && Number.isFinite(apiOff))
    ? apiOff
    : -new Date().getTimezoneOffset();
  React.useEffect(() => {
    setWorkSummaryClockOffset(tzOffsetMin);
    return () => setWorkSummaryClockOffset(null);
  }, [tzOffsetMin]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Value only read by the spine ("How the day ran"), currently removed —
  // the rail still writes it so restoring the spine is one destructure away.
  const [, setFocusedStarts] = React.useState([]);
  const [drawer, setDrawer] = React.useState(null);   // {type, payload}
  const [stripTick, setStripTick] = React.useState(0);
  const [dayKick, setDayKick] = React.useState(0);

  const report = useWorkSummaryReport();
  const showSec = report.show;
  const liveBrief = useDayBrief(
    (isMock || tab === "overview" || (report.ready && !showSec("aiBrief"))) ? null : member,
    date,
    source,
  );
  const brief = isMock ? MOCK_BRIEF : liveBrief;
  React.useEffect(() => {
    if (!showSec("hourlyTab") && tab === "hourly") setTab("story");
    if (!overviewOn && tab === "overview") setTab("story");
  }, [showSec, tab, overviewOn]);

  /* Who this reader is allowed to open — same access surface as before. */
  React.useEffect(() => {
    if (isMock) {
      setMembers(MOCK_MEMBERS);
      setMember(MOCK_MEMBERS[0].value);
      setDate(MOCK_DAY);
      setLoadingMembers(false);
      return undefined;
    }
    (async () => {
      setLoadingMembers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        const list = json?.data?.accessibleUsers || [];
        setMembers(list.map((u) => ({
          value: u._id || u.id,
          label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
          sub: u.position || u.email,
        })));
        const me = list.find((u) => u._id === user?._id) || list[0];
        if (me) setMember((cur) => (cur && cur.startsWith("mock-") ? me._id : (cur || me._id)));
      } catch (_) { /* the picker simply stays empty */ }
      finally { setLoadingMembers(false); }
    })();
  }, [user, isMock]);

  /* The one deterministic call that draws the page. */
  React.useEffect(() => {
    if (isMock) {
      setData(MOCK_DAY_DATA);
      setError(null);
      setLoading(false);
      setDrawer(null);
      const seed = [0, 0, 310, 480, 0, 390, 440, 510, 360, 0, 0, 420, 493, 0];
      for (let i = 13; i >= 0; i -= 1) {
        const dt = new Date(); dt.setDate(dt.getDate() - i);
        const key = fmtDate(dt);
        stripSet(MOCK_MEMBERS[0].value, source, key, seed[13 - i] || 0);
      }
      stripSet(MOCK_MEMBERS[0].value, source, MOCK_DAY, MOCK_DAY_DATA.totals.trackedMin);
      setStripTick((x) => x + 1);
      return undefined;
    }
    if (!member || String(member).startsWith("mock-")) return undefined;
    if (tab === "overview") {
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setFocusedStarts([]); setDrawer(null);
      try {
        const token = localStorage.getItem("token");
        const qs = new URLSearchParams({ userId: member, date, source });
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/day?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || !json?.success) throw new Error(json?.message || "Could not load this day.");
        setData(json.data);
        stripSet(member, source, date, json.data?.totals?.trackedMin || 0);
        setStripTick((x) => x + 1);
      } catch (e) {
        if (!cancelled) { setError(e.message || "Could not load this day."); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [member, date, source, isMock, tab, dayKick]);

  /* Export — declared AFTER `data`/`members` state (a callback above them
     reads `data` in the same render and trips the temporal dead zone). */
  const [exporting, setExporting] = React.useState(false);
  const [exportNote, setExportNote] = React.useState(null);
  const runExport = React.useCallback(async (kind) => {
    if (!member || exporting) return;
    setExporting(true); setExportNote(null);
    try { const f = await exportWorkSummary({ kind, userId: member, date, source, label: data?.person?.name || members.find((m) => m.value === member)?.label }); setExportNote(`Saved ${f}`); }
    catch (e) { setExportNote(e.message || "Export failed."); }
    finally { setExporting(false); setTimeout(() => setExportNote(null), 6000); }
  }, [member, date, source, exporting, data, members]);

  const totals = data?.totals;
  const kindMeta = KIND_META(t);
  const looks = (data?.threads || []).filter((th) => threadVerdict(th).level === "look");

  /* ── Drawer wiring ─────────────────────────────────────────────────── */
  const closeDrawer = () => setDrawer(null);
  const dayMin = Math.max(1, totals?.trackedMin || 1);

  /* Every drawer entry may carry `from` — the entry it was opened out of.
     The chain becomes the header breadcrumb; clicking a crumb restores that
     entry (with ITS own chain intact, so deeper trails keep working). */
  const crumbLabel = (d) => {
    if (!d) return "";
    if (d.type === "hour") return hourLabel(d.payload.hour);
    if (d.type === "thread") return d.payload.title || "Chapter";
    if (d.type === "app") return d.payload.name || "Application";
    if (d.type === "takeaway") return d.payload.title || "Takeaway";
    if (d.type === "block") return d.payload.app || "Session";
    return "";
  };
  const drawerCrumbs = [];
  for (let node = drawer?.from; node; node = node.from) {
    drawerCrumbs.unshift({ label: crumbLabel(node), node });
  }

  let drawerProps = null, drawerBody = null;
  if (drawer?.type === "thread") {
    const th = drawer.payload;
    const tone = C[th.cat] || C.browse;
    drawerProps = {
      tone,
      kicker: `Chapter · ${CATS[th.cat]?.label || th.cat}`,
      title: th.title,
      icon: <ToolIcon name={(th.apps || [])[0] || ""} cat={th.cat} size={26} />,
      chips: [
        fmtDur(th.minutes),
        `${Math.min(100, Math.round((th.minutes / dayMin) * 100))}% of the day`,
        (th.apps || []).slice(0, 3).join(" · ") || null,
      ],
    };
    drawerBody = (
      <ThreadDeepRead
        key={`${th.key}|${member}|${date}|${source}`}
        thread={th} bounds={data?.bounds} userId={member} date={date} source={source} tone={tone}
      />
    );
  } else if (drawer?.type === "takeaway") {
    const tk = drawer.payload;
    const m = kindMeta[tk.kind] || kindMeta.work;
    const tkShare = tk.shape && totals?.trackedMin ? Math.round((tk.shape.minutes / totals.trackedMin) * 100) : null;
    drawerProps = {
      tone: m.color,
      kicker: `Takeaway · ${m.tag}${tk.occupiesTime === false ? " · observation" : ""}`,
      title: tk.title,
      icon: m.icon ? <m.icon size={23} /> : null,
      chips: [
        tk.stat || null,
        tkShare != null ? `${tkShare}% of the day` : null,
        tk.shape?.sessions ? `${tk.shape.sessions} session${tk.shape.sessions === 1 ? "" : "s"}` : null,
        tk.shape?.windowFrom != null ? `${fmtClock(tk.shape.windowFrom)} – ${fmtClock(tk.shape.windowTo)}` : null,
      ],
    };
    drawerBody = (
      <TakeawayDeepRead
        key={`${tk.id}|${member}|${date}|${source}`}
        tk={tk} userId={member} date={date} source={source} tone={m.color}
      />
    );
  } else if (drawer?.type === "app") {
    const a = drawer.payload;
    const tone = C[a.cat] || C.browse;
    drawerProps = {
      tone,
      kicker: `Application · ${CATS[a.cat]?.label || a.cat}`,
      title: a.name,
      icon: <ToolIcon name={a.name} type={a.type} cat={a.cat} size={26} />,
      chips: [fmtDur(a.minutes), totals ? `${Math.round((a.minutes / dayMin) * 100)}% of the day` : null],
    };
    drawerBody = (
      <AppDetail
        key={`${a.name}|${member}|${date}|${source}`}
        app={a.name} tone={tone} userId={member} date={date} source={source} sf={sf}
      />
    );
  } else if (drawer?.type === "hour") {
    const h = drawer.payload;
    drawerProps = hourDrawerProps(h, C, <ToolIcon name={h.apps[0]?.name || ""} cat={h.domCat} size={26} />);
    drawerBody = (
      <HourDeepRead
        key={`${h.hour}|${member}|${date}|${source}`}
        hour={h} userId={member} date={date} source={source}
        tone={C[h.domCat] || C.browse}
        offsetMin={tzOffsetMin}
        bounds={data?.bounds}
        onOpenThread={(key) => {
          const th = (data?.threads || []).find((x) => x.key === key);
          // Carry the hour along: the chapter drawer shows it as a breadcrumb
          // and clicking the crumb returns here.
          if (th) setDrawer({ type: "thread", payload: th, from: drawer });
        }}
      />
    );
  } else if (drawer?.type === "block") {
    const b = drawer.payload;
    const tone = C[b.cat] || C.browse;
    drawerProps = {
      tone,
      kicker: `Session · ${CATS[b.cat]?.label || b.cat}`,
      title: b.title || b.app || "One stretch of the day",
      icon: <ToolIcon name={b.app || ""} cat={b.cat} size={26} />,
      chips: [`${fmtClock(b.startMs)} – ${fmtClock(b.endMs)}`, fmtDur(b.minutes), b.src === "agent" ? "Background agent" : "Desktop app"],
    };
    drawerBody = (
      <div>
        <DrawerSec title="Position in the day">
          <DrawerSpine
            spans={[{ startMs: b.startMs, endMs: b.endMs }]} bounds={data?.bounds} tone={tone}
            startLabel={data?.bounds ? fmtClock(data.bounds.startMs) : ""} endLabel={data?.bounds ? fmtClock(data.bounds.endMs) : ""}
          />
        </DrawerSec>
        <DrawerSec title="How it ran">
          <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.7 }}>
            {b.activityLabel} activity · {b.steadiness?.toLowerCase() || "steady"} ·
            {" "}{b.app}{b.url ? ` · ${b.url}` : ""}
          </div>
        </DrawerSec>
        {b.artifacts?.length > 0 && (
          <DrawerSec title="What was in front of them">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {b.artifacts.map((a) => (
                <span key={a} style={{
                  fontSize: 11, color: t.c.textSecondary, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
                  borderRadius: t.r.sm, padding: "4px 9px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }} title={a}>{a}</span>
              ))}
            </div>
          </DrawerSec>
        )}
        {b.threadKey && data?.threads && (
          <DrawerSec title="Part of">
            {(() => {
              const th = data.threads.find((x) => x.key === b.threadKey);
              if (!th) return <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>—</span>;
              const Icon = CAT_ICON[th.cat] || Sparkles;
              return (
                <button
                  type="button" className="st-reset st-focusable st-hit"
                  onClick={() => setDrawer({ type: "thread", payload: th })}
                  style={{
                    display: "flex", alignItems: "center", gap: 11, cursor: "pointer", width: "100%",
                    background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover,
                    border: `1px solid ${t.c.border}`,
                    borderRadius: t.r.md, padding: "11px 13px",
                  }}
                >
                  <span style={{ width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center", color: tone, background: `${tone}${t.isDark ? "26" : "15"}`, flex: "none" }}>
                    <Icon size={15} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                    <span style={{ display: "block", fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{th.title}</span>
                    <span style={{ display: "block", fontSize: t.font.size.xs, color: t.c.textTertiary }}>{fmtDur(th.minutes)} across the day — open the chapter</span>
                  </span>
                </button>
              );
            })()}
          </DrawerSec>
        )}
      </div>
    );
  }

  const exportBtn = (
    <button type="button" className="st-reset st-focusable st-hit-icon" disabled={exporting || loading || !data}
      onClick={() => runExport(tab === "hourly" ? "hourly" : "day")}
      title={tab === "hourly" ? "Export hour by hour as PDF" : "Export the day's story as PDF"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600,
        padding: "7px 12px", borderRadius: 8, border: `1px solid ${t.c.border}`, background: t.c.surface,
        color: t.c.textSecondary, cursor: exporting || loading || !data ? "not-allowed" : "pointer",
        opacity: exporting || loading || !data ? 0.6 : 1,
      }}><Download size={14} /> {exporting ? "Building PDF…" : "Export PDF"}</button>
  );

  const headerTools = (
    <div className="tg-filterbar" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <DateRangeTool
        range={dateToRange(date)}
        onChange={(r) => {
          const next = fmtDate(r?.end || r?.start || new Date());
          if (isMock && next !== MOCK_DAY) setMock(false);
          setDate(next);
        }}
        layoutId="wsNewRange"
        maxDate={new Date()}
        maxSpanDays={1}
        presets={[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
        ]}
      />
      {scope === "person" && tab !== "overview" && showSec("aiBrief") && (
        <Button
          variant="secondary"
          leftIcon={<RefreshCw size={14} />}
          disabled={!member || brief.running || loading || loadingMembers}
          onClick={() => { setDayKick((n) => n + 1); brief.regenerate?.(); }}
        >
          {brief.running ? "Writing…" : "Regenerate"}
        </Button>
      )}
      {scope === "person" && tab !== "overview" ? exportBtn : null}
    </div>
  );

  return (
    <PageShell
      eyebrow={scope === "department" ? "AI · Department" : "AI · Person"}
      title="Work summary"
      subtitle={scope === "department"
        ? "One department's day, end to end"
        : "One person's day — desktop app and background agent"}
      icon={<Sparkles size={22} />}
      tabs={scope === "person" && (showSec("hourlyTab") || overviewOn) ? [
        { key: "story", label: "The day" },
        ...(showSec("hourlyTab") ? [{ key: "hourly", label: "Hour by hour" }] : []),
        ...(overviewOn ? [{ key: "overview", label: "Get overview" }] : []),
      ] : undefined}
      activeTab={tab}
      onTabChange={setTab}
      actions={headerTools}
      topRight={deptScopeEnabled ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {[["person", "Person", <UserIcon key="u" size={13} />], ["department", "Department", <Building2 key="b" size={13} />]].map(([k, lbl, ic]) => (
            <button key={k} type="button" className="st-reset st-focusable" onClick={() => setScope(k)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: scope === k ? 500 : 400,
                padding: "0 8px", height: 34, cursor: "pointer",
                color: scope === k ? t.c.text : t.c.textSecondary, background: "transparent",
                borderBottom: scope === k ? `2px solid ${t.c.icon}` : "2px solid transparent",
              }}>{ic}{lbl}</button>
          ))}
          {scope === "person" && backTo && (
            <button type="button" className="st-reset st-focusable" onClick={() => setScope("department")}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 600, color: t.c.primary, cursor: "pointer" }}>
              <ChevronLeft size={14} /> {backTo}
            </button>
          )}
        </div>
      ) : undefined}
    >
      <StoryStyles />
      <div style={canvasStyle(sf)}>
        <div style={ambientStyle(sf)} aria-hidden="true" />
        <div style={{ position: "relative", ...storyVars(t) }}>
          {exportNote && (
            <div style={{ fontSize: 12, color: t.c.textTertiary, marginBottom: 10 }}>{exportNote}</div>
          )}

          {scope === "department" && (
            <DepartmentStory date={date} onDate={setDate} source={source} sf={sf}
              onOpenPerson={openPersonFromDept} initialDepartment={backTo} />
          )}

          {scope === "person" && (
          <React.Fragment>
          {/* ⓪ the headline */}
          <StoryHero
            members={members} member={member} onMember={setMember} loadingMembers={loadingMembers}
            date={date} onDate={(d) => { if (isMock && d !== MOCK_DAY) setMock(false); setDate(d); }} data={data} loading={loading} sf={sf} refreshStrip={stripTick}
            looks={looks} onOpenLook={(th) => setDrawer({ type: "thread", payload: th })}
            pickerOnly={tab === "overview"}
            overviewHours={tab === "overview" ? overviewHours : null}
          />

          {!member && !loadingMembers && (
            <Card><EmptyState icon={<Users size={22} />} title="Choose someone to see their day"
              description="Pick a team member above. Their day is reconstructed from both sources at once." /></Card>
          )}

          {error && tab !== "overview" && (
            <Card><EmptyState icon={<Users size={22} />} title="Could not load this day" description={error} /></Card>
          )}

          {member && tab === "overview" && (
            <DayOverview
              member={member}
              date={date}
              source={source}
              personName={data?.person?.name || members.find((m) => m.value === member)?.label}
              onHours={setOverviewHours}
            />
          )}

          {member && !error && tab !== "overview" && (
            <React.Fragment>
              {tab === "hourly" && data?.tz?.label && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 500, color: t.c.textTertiary, marginBottom: t.s[3] }}>
                  <i style={{ width: 6, height: 6, borderRadius: "50%", background: t.status.positive }} />
                  All times in the organisation's timezone — {data.tz.label}
                </div>
              )}

              {tab === "hourly" ? (
                !loading && totals && !totals.trackedMin ? (
                  <Card style={{ border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow }}>
                    <EmptyState icon={<CalendarDays size={22} />} title="Nothing tracked on this day"
                      description="Neither the desktop app nor the background agent reported activity for this person." />
                  </Card>
                ) : (
                  <SectionFrame
                    kicker="Timeline"
                    title="Hour by hour"
                    sub="Every worked hour, as it ran. Open one for the deep read."
                    footer={!loading && (data?.bounds || true) ? (
                      <div style={{ padding: "10px 20px 14px", borderTop: `1px solid ${sf.hairline}` }}>
                        <HourLegend />
                        {data?.bounds && (
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 500, color: t.c.textTertiary, marginTop: 8 }}>
                            <i style={{ width: 6, height: 6, borderRadius: "50%", background: t.c.textTertiary }} />
                            Hours outside {fmtClockTz(data.bounds.startMs, tzOffsetMin)} – {fmtClockTz(data.bounds.endMs, tzOffsetMin)} had no tracked activity and aren't listed.
                          </div>
                        )}
                      </div>
                    ) : null}
                  >
                    <HourGrid
                      data={data} loading={loading}
                      offsetMin={tzOffsetMin}
                      onOpenHour={(h) => setDrawer({ type: "hour", payload: h })}
                    />
                  </SectionFrame>
                )
              ) : (
              <React.Fragment>
              {showSec("pulse") && <StoryPulse data={data} loading={loading} />}

              {!loading && totals && !totals.trackedMin ? (
                <Card style={{ marginTop: t.s[4], border: `1px solid ${sf.cardBorder}`, boxShadow: sf.cardShadow }}>
                  <EmptyState icon={<CalendarDays size={22} />} title="Nothing tracked on this day"
                    description="Neither the desktop app nor the background agent reported activity for this person." />
                </Card>
              ) : (
                <React.Fragment>
                  {/* "How the day ran" (StorySpine) removed on request.
                      To restore: re-import StorySpine and render it here with
                      data/loading/focusedStarts/onOpenBlock/sf as before. */}

                  {(showSec("whatWentInto") || showSec("aiAdoption")) && (
                  <div className={showSec("whatWentInto") && showSec("aiAdoption") ? "st-sec-pair" : undefined}>
                    {showSec("whatWentInto") && (
                    <SectionFrame
                      kicker="Picture"
                      title="What it went into"
                      sub="By kind of work, not by application."
                    >
                      <div style={{ padding: "16px 20px 20px" }}>
                        {loading ? <WhatWentIntoSk /> : <WhatWentInto
                          blocks={data?.blocks}
                          onOpenApp={(name) => {
                            const a = (data?.apps || []).find((x) => x.name === name);
                            if (a) setDrawer({ type: "app", payload: a });
                          }}
                        />}
                      </div>
                    </SectionFrame>
                    )}

                    {showSec("aiAdoption") && (
                    <SectionFrame
                      kicker="AI"
                      tone={t.c.ai}
                      title="AI adoption"
                      sub="Which AI tools they sat in, and how much of the day that was."
                      meta={loading ? null : aiAdoptionMeta(data?.blocks, data?.apps, totals)}
                    >
                      <AiAdoption
                        loading={loading}
                        blocks={data?.blocks}
                        apps={data?.apps}
                        totals={totals}
                        offsetMin={tzOffsetMin}
                        onOpenApp={(name) => {
                          const a = (data?.apps || []).find((x) => x.name === name)
                            || { name, minutes: 0, cat: "deep" };
                          setDrawer({ type: "app", payload: a });
                        }}
                      />
                    </SectionFrame>
                    )}
                  </div>
                  )}

                  {showSec("acrossTheDay") && (
                  <SectionFrame
                    kicker="Work"
                    title="Across the day"
                    sub="The work itself — projects, kind of work, and what got touched."
                    meta={!loading && data?.threads?.length ? `${data.threads.length} threads` : null}
                  >
                    <div style={{ padding: "12px 16px 14px" }}>
                      {loading ? <DayJuiceSk /> : <DayJuice
                        threads={data?.threads}
                        blocks={data?.blocks}
                        totals={totals}
                        bounds={data?.bounds}
                        offsetMin={tzOffsetMin}
                        onOpen={(th) => setDrawer({ type: "thread", payload: th })}
                      />}
                    </div>
                  </SectionFrame>
                  )}

                  {(showSec("aiBrief") || showSec("timeByApp")) && (
                  <div className={showSec("aiBrief") && showSec("timeByApp") ? "st-sec-pair" : undefined}>
                    {showSec("aiBrief") && (
                    <SectionFrame
                      kicker="AI brief"
                      tone={t.c.ai}
                      title="What stood out"
                      sub="The written brief. Open a note for the full takeaway."
                      meta={brief.takeaways?.length ? `${brief.takeaways.length} notes` : (brief.running ? "Writing…" : null)}
                    >
                      <StoryRail nested brief={brief} bounds={data?.bounds} onFocusStretches={setFocusedStarts}
                        onOpen={(tk) => setDrawer({ type: "takeaway", payload: tk })} />
                    </SectionFrame>
                    )}

                    {showSec("timeByApp") && (
                    <SectionFrame
                      kicker="Ledger"
                      title="Time by app"
                      sub="Where the hours actually went, ranked."
                      meta={data?.apps?.length ? `${data.apps.length} apps` : null}
                    >
                      <StoryFlow apps={data?.apps} totals={totals} loading={loading}
                        onOpenApp={(a) => setDrawer({ type: "app", payload: a })} />
                    </SectionFrame>
                    )}
                  </div>
                  )}
                </React.Fragment>
              )}
              </React.Fragment>
              )}
            </React.Fragment>
          )}
          </React.Fragment>
          )}
        </div>
      </div>

      <StoryDrawer
        open={!!drawer} onClose={closeDrawer}
        crumbs={drawerCrumbs.map((c) => ({ label: c.label, onClick: () => setDrawer(c.node) }))}
        {...(drawerProps || {})}
      >
        {drawerBody}
      </StoryDrawer>
    </PageShell>
  );
}
